import { Badge } from "@/components/ui/badge";
import { TimesheetEntry } from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import { DailyHours } from "@/v2/types";
import { eachDayOfInterval, format, parseISO } from "date-fns";

const STATUS_UI: Record<
    DailyHours["status"],
    { label: string; className: string; legend: string }
> = {
    PRESENT: {
        label: "P",
        className: "bg-[#FFFF00] text-black",
        legend: "Present / Hours Worked",
    },

    ABSENT: {
        label: "A",
        className: "bg-[#FFA500] text-black",
        legend: "Absent",
    },

    SICK_LEAVE: {
        label: "SL",
        className: "bg-[#FFB6C1] text-black",
        legend: "Sick Leave",
    },

    VACATION: {
        label: "V",
        className: "bg-[#fef08a] text-black",
        legend: "Vacation / Annual Leave",
    },

    HOLIDAY: {
        label: "H",
        className: "bg-[#90EE90] text-black",
        legend: "Holiday",
    },

    DAY_OFF: {
        label: "DO",
        className: "bg-[#ADD8E6] text-black",
        legend: "Day Off",
    },

    NOT_MOBILIZED: {
        label: "NM",
        className: "bg-[#808080] text-white",
        legend: "Not Mobilized",
    },

    RELEASED: {
        label: "R",
        className: "bg-[#FF0000] text-white",
        legend: "Released",
    },

    PUNCH_MISS: {
        label: "PM",
        className: "bg-[#9333ea] text-white",
        legend: "Punch Miss",
    },
};

const AttendanceTableLegend = () => {
    return (
        <div className="grid grid-cols-3 place-content-center gap-5">
            {Object.values(STATUS_UI).map((status) => (
                <div key={status.label} className="flex items-center gap-2">
                    <span
                        className={`inline-flex h-6 w-6 items-center justify-center rounded text-xs font-semibold ${status.className}`}
                    >
                        {status.label}
                    </span>
                    <span className="text-muted-foreground">
                        {status.legend}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default function AttendanceTable({
    attendanceDetails,
    startDate,
    endDate,
}: {
    attendanceDetails: TimesheetEntry[];
    startDate: string;
    endDate: string;
}) {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    const dayRange = eachDayOfInterval({ start, end });
    const formattedDays = dayRange.map((date) => ({
        weekDay: format(date, "EEEE"),
        day: format(date, "dd"),
    }));

    const totalDays = formattedDays.length;

    return (
        <div className="flex flex-col gap-5">
            <div className="overflow-hidden rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted">
                            <tr>
                                <th className="sticky left-0 z-10 border-r bg-muted px-4 py-3 text-left font-semibold">
                                    Employee
                                </th>
                                <th className="px-4 py-3 text-left font-semibold">
                                    Trade
                                </th>
                                <th className="px-4 py-3 text-left font-semibold">
                                    Iqama
                                </th>
                                <th className="px-4 py-3 text-center font-semibold">
                                    Rate/Hr
                                </th>
                                {formattedDays.map((log, idx) => (
                                    <th
                                        key={idx}
                                        className="min-w-[60px] px-2 py-3 text-center font-semibold"
                                    >
                                        <div className="flex flex-col">
                                            <span className="text-xs">
                                                {log.weekDay}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {log.day}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-center font-semibold">
                                    Total Hours
                                </th>
                                <th className="px-4 py-3 text-center font-semibold">
                                    Total Days
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendanceDetails.map((emp, empIdx) => {
                                const totalHours = emp.total_hours;
                                const totalPresent = emp.work_logs.filter(
                                    (log) => log.status === "PRESENT",
                                ).length;

                                return (
                                    <tr
                                        key={empIdx}
                                        className="border-t hover:bg-muted/50"
                                    >
                                        <td className="sticky left-0 z-10 border-r bg-background px-4 py-3 font-medium">
                                            {emp.employee_name}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge variant="outline">
                                                {emp.trade}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                                            {emp.iqama}
                                        </td>
                                        <td className="px-4 py-3 text-center font-semibold">
                                            ${emp.hourly_rate}
                                        </td>

                                        {emp.work_logs.map((log, logIdx) => {
                                            const ui = STATUS_UI[log.status];

                                            return (
                                                <td
                                                    key={logIdx}
                                                    className="px-2 py-3 text-center"
                                                >
                                                    <span
                                                        className={`inline-flex h-8 w-8 items-center justify-center rounded font-semibold ${ui.className}`}
                                                        title={log.status}
                                                    >
                                                        {log.status ===
                                                        "PRESENT"
                                                            ? (log.hours ??
                                                              ui.label)
                                                            : ui.label}
                                                    </span>
                                                </td>
                                            );
                                        })}

                                        <td className="px-4 py-3 text-center font-bold">
                                            {totalHours}h
                                        </td>
                                        <td className="px-4 py-3 text-center font-bold">
                                            {totalPresent}/{totalDays}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <AttendanceTableLegend />
        </div>
    );
}
